// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { NavLink, Link } from 'react-router-dom';
import cx from 'classnames';
import { withStyles } from "tss-react/mui";
import { Drawer, IconButton, Tooltip, List, ListItemButton, ListItemIcon, ListItemText, alpha } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getTestAttr } from '@shared/utils/common';
import Logo from '@shared/icons/Logo';
import ROUTES from '@shared/constants/routes';
import VersionTag from '@shared/components/VersionTag';
import { colorsv5 } from '@core/themev5';

// Struct to hold a menu item.
export interface Item {
  name: string;
  route: string;
  icon: React.ReactNode;
  roles: Array<string>;
  onlyForHosting: boolean;
  subItems?: Array<Item>;
  isSub: boolean;
}

// Props for the main class.
export interface NavigationMenuProps {
  className?: string;
  classes?: Record<string, string>;
  items: Array<Item>;
  onToggle: (isOpen: boolean) => any;
  appVersion?: string;
}

interface NavigationMenuState {
  open: boolean;
}

const STATE_KEY = 'VI_MENU_STATE';

export const isMenuOpen = () => Boolean(localStorage.getItem(STATE_KEY));

// Class to show the navigation menu on the left side of the screen.
class NavigationMenu extends React.Component<NavigationMenuProps, NavigationMenuState> {
  state: NavigationMenuState = {
    open: isMenuOpen(),
  };

  // Toggle expand/collapse menu display.
  private handleToggle = (): void => {
    this.setState(
      (state) => ({ ...state, open: !state.open }),
      () => {
        const { open } = this.state;

        this.props.onToggle(open);

        if (open) {
          localStorage.setItem(STATE_KEY, 'true');

          return;
        }

        localStorage.removeItem(STATE_KEY);
      }
    );
  };

  render() {
    const { items = [] } = this.props;
    const { open } = this.state;
    const classes = withStyles.getClasses(this.props);

    // Flatten menu items and subitems
    const allItems = new Array<Item>();
    items.forEach(item => {
      allItems.push(item);
      item.subItems?.forEach(subItem => {
        allItems.push(subItem);
      })
    })

    return (
      <div
        className={cx(classes.root, {
          [classes.rootOpen]: open,
          [classes.rootClose]: !open,
        })}
      >
        {/* <Drawer
          variant="permanent"
          className={classes.drawer}
          classes={{
            paper: cx({
              [classes.drawerOpen]: open,
              [classes.drawerClose]: !open,
            }),
          }}
        > */}
          {/* VI logo @ upper left corner */}
          <div className={cx(classes.toolbar, classes.flex, classes.alignItemsCenter, classes.justifyContentCenter, classes.minHeight)}>
            <Link to={ROUTES.initial}>
              <Logo small={!open} className={open ? classes.logo : classes.logoSmall} />
            </Link>
            <div 
              {...getTestAttr('layout', 'nav-toggle', 'btn')}
              className={cx(classes.menuButton, open ? classes.menuButtonOpen : classes.menuButtonClosed)}
              onClick={this.handleToggle}
            >
              {open
                ? <ChevronLeftIcon style={{ fontSize: 24 }} />
                : <ChevronRightIcon style={{ fontSize: 24 }} />
              }
            </div>
          </div>

          <div className={cx(classes.content, classes.flex, classes.flexColumn)}>
            { // May array of menu items to list of links.
              allItems.map((item) => (
                <List disablePadding key={item.route.concat(item.isSub ? '_sub' : '')} className={classes.list}>
                  <Tooltip title={open ? '' : item.name} placement="right">
                    <NavLink
                      {...getTestAttr(item.name, 'nav-link', 'btn')}
                      to={item.route}
                      className={cx(classes.navLink, classes.fullWidth, item.isSub ? classes.subItem : '')}
                      activeClassName={classes.activeRouteClass}
                    >
                      <ListItemIcon className={cx(classes.listItemIcon, classes.alignItemsCenter, classes.justifyContentCenter)}>
                        {item.icon}
                      </ListItemIcon>
                      {open && (
                        <ListItemText
                          primary={item.name}
                          classes={{
                            primary: cx(classes.listItemTextRoot, classes.ellipsisText),
                            root: classes.listItemText
                          }}
                        />
                      )}
                    </NavLink>
                  </Tooltip>                
                </List>
            ))}

            {/* Version number at lower left corner. */}
            <VersionTag maxWidth="calc(100% - 12px)" />
          </div>
        {/* </Drawer> */}
      </div>
    );
  }
}


const drawerWidthOpen = 200;
const drawerWidthClose = 64;
const toolbarHeight = 80;
const itemHeight = 40;
const listItemBase = {
  borderLeft: `3px solid transparent`,

  '& $listItemTextRoot': {
    color: 'black',
    fontSize: 15,
    fontWeight: 'bold',
    height: '100%',
    lineHeight: `${itemHeight}px`,
  }
};

const listItemBaseHover = {
  color: colorsv5.primaryAccent,
  backgroundColor: 'rgba(209, 12, 12, 0.04)',
  borderLeft: `3px solid ${colorsv5.primaryAccent}`,

  '& $listItemTextRoot': {
    color: colorsv5.primaryAccent,
  },

  '& svg g': {
    opacity: 1,

    '& > *': {
      fill: colorsv5.primaryAccent,
    }
  }
};

const NavigationMenuStyled = withStyles(
  NavigationMenu, 
  (theme, props) => ({
    "root": {
      position: 'relative' as const,
      transition: 'width 0.5s',
      zIndex: 2
    },
    "rootOpen": {
      width: drawerWidthOpen,

      '& $menuButton': {
        opacity: 0,
      },

      '&:hover': {
        '& $menuButton': {
          opacity: 1,
        }
      }
    },
    "rootClose": {
      width: drawerWidthClose,
    },
    "menuButton": {
      color: 'black',
      position: 'absolute' as const,
      boxShadow: '0px 4px 8px rgba(68, 68, 68, 0.16)',
      backgroundColor: 'transparent',
      // fontSize: 24,
      paddingTop: '2px',
      padding: '0px 1px',
      height: 28,
      width: 28,
      textAlign: 'center' as const,
      verticalAlign: 'middle',
      zIndex: 3,
      transition: `${theme.transitions.duration.standard}s`,
      borderRadius: '55%',
      cursor: 'pointer',

      '&:hover': {
        backgroundColor: 'white',
        boxShadow: '0px 4px 8px rgba(68, 68, 68, 0.66)',
      }
    },
    "menuButtonOpen": {
      left: drawerWidthOpen - 12,
    },
    "menuButtonClosed": {
      left: drawerWidthClose - 12,
      backgroundColor: 'white',
    },
    "drawer": {
      display: 'block',
      height: '100%',
    },
    "drawerOpen": {
      width: drawerWidthOpen,
      border: 'none',
      overflow: 'visible',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    "drawerClose": {
      width: drawerWidthClose,
      border: 'none',
      overflow: 'visible',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
    "logo": {
      width: 154,
      height: 'auto',
    },
    "logoSmall": {
      paddingLeft: theme.spacing(3),
      width: 16,
      height: 'auto',
    },
    "toolbar": {
      overflow: 'hidden',
      padding: theme.spacing(0, 3, 0, 1),
      background: theme.palette.colors.greyLight5,
      boxShadow: '2px 0px 4px rgba(0, 0, 0, .08)',
      // ...mixins.toolbar,
      height: toolbarHeight,
      borderBottom: `1px solid ${alpha(theme.palette.colors.greyDark, .08)}`,
      boxSizing: 'content-box' as const,
    },
    "content": {
      flexGrow: 1,
      flex: 'auto',
      background: theme.palette.colors.greyLight5,
      boxShadow: '2px 0px 4px rgba(0, 0, 0, .08)',
      overflowY: 'auto' as const,
      overflowX: 'hidden' as const,
      height: 'calc(100vh - 136px)',
      paddingTop: theme.spacing(3),

      '&::-webkit-scrollbar': {
        width: 0
      }
    },
    "list": {
      width: '100%',
      padding: 0,
    },
    // "sublist": {
    //   display: 'block',
    //   width: '100%',
    //   padding: theme.spacing(0, 0.1),
    // },
    "subItem": {
      paddingLeft: theme.spacing(4),
    },
    "listItem": {
      padding: 0,
      cursor: 'default',

      '&:hover': {
        backgroundColor: 'inherit',
      }
    },
    "listItemIcon": {
      // minWidth: 16,
      // marginRight: theme.spacing(2),

      '& svg': {
        fontSize: 18,
      },
    },
    "listItemText": {
      margin: 0,
    },
    "listItemTextRoot": {
      fontSize: 15,
      fontWeight: 'bold',
      paddingTop: theme.spacing(2),
    },
    "navLink": {
      // padding: theme.spacing(0, 3),
      height: itemHeight,
      cursor: 'pointer',
      ...listItemBase,

      '&:hover': {
        ...listItemBaseHover,
      }
    },
    "activeRouteClass": {
      cursor: 'default',
      pointerEvents: 'none' as const,
      ...listItemBaseHover,
    },
    "flex": {
      display: 'flex',
    },
    "flexColumn": {
      flexDirection: 'column' as const,
    },
    "alignItemsCenter": {
      alignItems: 'center',
    },
    "justifyContentCenter": {
      justifyContent: 'center',
    },
    "fullWidth": {
      width: '100%',
    },
    "minHeight": {
      minHeight: toolbarHeight,
    },
    "ellipsisText": {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  })
);

export default NavigationMenuStyled;